<template>
  <div id="companies">
    <div id="container" v-if="company.loaded && !company.error">
      <div class="box shadow">
        <topdata v-bind:data="topData"></topdata>
      </div>

      <div class="column column-33 no-padding-left">
        <transition name="fade">
          <div class="box shadow" v-if="company.data.description">
            <h2>{{ $t('extra-info' )}}</h2>
            <p>{{ company.data.description }}</p>
          </div>
        </transition>

        <div class="box shadow">
          <h2>{{ $t('data') }}</h2>

          <table>
            <tr>
              <td class="valign-top">{{ $t('address') }}</td>
              <td>
                <a :href="$parent.getDirectionsUrl(company.data.address)" target="_blank">
                  {{ $t('address-firstline', {
                        street: company.data.address.street,
                        number: company.data.address.number,
                        zipcode: company.data.address.zipcode,
                        city: company.data.address.city,
                        state: company.data.address.state,
                        country: company.data.address.country,
                  }) }}<br/>
                  {{ $t('address-secondline', {
                        street: company.data.address.street,
                        number: company.data.address.number,
                        zipcode: company.data.address.zipcode,
                        city: company.data.address.city,
                        state: company.data.address.state,
                        country: company.data.address.country,
                  }) }}<br/>
                  {{ $t('address-thirdline', {
                        street: company.data.address.street,
                        number: company.data.address.number,
                        zipcode: company.data.address.zipcode,
                        city: company.data.address.city,
                        state: company.data.address.state,
                        country: company.data.address.country,
                  }) }}
                </a>
              </td>
            </tr>

            <tr>
              <td>{{ $t('email') }}</td>
              <td><a :href="`mailto:${company.data.email}`">{{ company.data.email }}</a></td>
            </tr>

            <tr>
              <td>{{ $t('phone') }}</td>
              <td><a :href="`tel:${company.data.phone}`">{{ company.data.phone }}</a></td>
            </tr>

            <tr v-if="company.data.coc">
              <td>{{ $t('coc-number') }}</td>
              <td>{{ company.data.coc }}</td>
            </tr>

            <tr v-if="company.data.vat">
              <td>{{ $t('vat-number') }}</td>
              <td>{{ company.data.vat }}</td>
            </tr>

            <tr v-if="company.data.website">
              <td>{{ $t('website') }}</td>
              <td>{{ company.data.website }}</td>
            </tr>
          </table>
        </div>

        <div class="box shadow">
          <h2>Interne notities</h2>

          <transition name="fade">
            <div class="internal-messages" v-show="company.loaded">
              <message v-for="(message, index) in company.data.internal_messages"
                       :message="message"
                       :key="index"></message>

              <div class="messages-wrapper">
                <div class="no-messages"
                     v-show="!hasMessages(company.data)">
                  {{ $t('no-internal-messages') }}
                </div>
              </div>

              <div class="new-message shadow">
                <form v-on:submit.prevent="sendNewInternalMessage">
                  <div class="attachment">
                    <label for="new-message-file"><i class="fal fa-paperclip clickable"></i></label>
                    <input id="new-message-file"
                           type="file"
                           multiple
                           ref="internalMessageFile"
                           v-on:change="handleFileUpload()">
                  </div><!--
                  --><div class="message-text">
                    <input type="text"
                           v-model="newInternalMessage.message"
                           placeholder="Typ een bericht...">
                  </div><!--
                  --><div class="send-button">
                    <button class="clickable">Verzenden</button>
                  </div>
                </form>
              </div>

              <transition name="fade">
                <div class="uploadedFiles" v-show="newInternalMessage.files.length > 0">
                  <h3>{{ $t('added-files') }}</h3>
                  <div class="uploadedFile"
                       v-for="(file, index) in newInternalMessage.files"
                       :key="index">
                    <i class="fal fa-file"></i> {{ file.name }}<br/><br/>
                  </div>
                </div>
              </transition>
            </div>
          </transition>

          <div class="loader" v-if="!company.loaded"></div>
        </div>
      </div><!--

      --><div class="column column-66 no-padding-right">
      <div class="box shadow">
        <h2>{{ $tc('company-contacts', company.data.contacts.length) }}</h2>

        <div class="no-contacts" v-if="company.data.contacts.length === 0">
          {{ $t('no-contacts') }}
        </div>

        <div class="multi-contacts" v-if="company.data.contacts.length > 0">
          <transition-group name="fade">
            <div class="inline-block  contact-summary-wrapper"
                 v-for="(contact, index) in company.data.contacts"
                 v-show="index < 4 || showExtraContacts"
                 style="width: 50%;"
                 :key="contact._id">
              <router-link class="contact-summary clickable shadow"
                   tag="div"
                   to="/contacts">
                <div class="contact-thumb"><img :src="contact.image" class="round"/></div>

                <div class="contact-info">
                  <div class="contact-name"><h4 class="accent-color">{{ contact.fullName }}</h4></div>

                  <div class="contact-email" v-if="contact.emails">
                    {{ contact.emails[0] }}
                    <sup v-if="contact.emails.length > 1">+{{contact.emails.length - 1}}</sup>
                  </div>

                  <div class="contact-phone" v-if="contact.phones">
                    {{ contact.phones[0] }}
                    <sup v-if="contact.phones.length > 1">+{{contact.phones.length - 1}}</sup>
                  </div>
                </div>
              </router-link>
            </div>
          </transition-group>

          <div class="show-extra center" v-if="company.data.contacts.length > 4 && !showExtraContacts">
            <button class="button button-default" @click="showExtraContacts = true">Alles weergeven</button>
          </div>
        </div>


      </div>

      <div class="box shadow">
        <h2>{{ $t('communication') }}</h2>

        <transition name="fade">
          <div class="content" v-show="company.loaded">
            <message v-for="(message, index) in company.data.communication"
                     :message="message"
                     :key="index"></message>

            <div class="no-messages"
                 v-if="!hasCommunication(company.data)">
              {{ $t('no-messages') }}
            </div>
          </div>
        </transition>


      </div>
    </div>

    </div>

    <div v-else-if="company.loaded && company.error" class="container">
      <div class="box shadow">
        <error-message></error-message>
      </div>
    </div>

    <div class="loader" v-else></div>

  </div>
</template>

<script>
import { Companies } from '@/services/api/ApiRoutes';

export default {
  name: 'SingleCompany',

  data() {
    // Set page title
    this.$parent.title.heading = 'Bedrijf';
    this.$parent.title.subheading = '';

    return {
      topData: [],
      company: {
        error: null,
        data: [],
        loaded: false,
      },
      newInternalMessage: {
        message: '',
        files: [],
      },
      showExtraContacts: false,
    };
  },

  mounted() {
    this.getCompany(this.$route.params.id);
  },

  methods: {
    getCompany(id) {
      Companies.getOne(id).then((company) => {
        this.company.error = null;
        this.company.data = company;

        this.$parent.title.subheading = company.name;
        this.updateTopData(company);

        this.company.loaded = true;
      }).catch((error) => {
        this.company.error = error;
        this.company.loaded = true;
      });
    },

    updateTopData(company) {
      this.topData = [
        {
          title: this.$t('company'),
          value: company.name,
          icon: 'fa-building',
          link: `/companies/${company._id}`,
        },
        {
          title: this.$tc('contact-moment', company.communication.length),
          value: company.communication.length,
          icon: 'fa-comments',
          link: `/companies/${company._id}`,
        },
        {
          title: this.$t('last-contact'),
          value: this.$moment(company.communication[0].created_at).fromNow(),
          icon: 'fa-user',
          link: `/companies/${company._id}`,
        },
        {
          title: this.$t('created-at'),
          value: this.$moment(company.created_at).fromNow(),
          icon: 'fa-calendar',
          link: `/companies/${company._id}`,
        },
      ];
    },

    hasMessages(company) {
      try {
        return company.internal_messages.length > 0;
      } catch (e) {
        return false;
      }
    },

    hasCommunication(company) {
      try {
        return company.communication.length > 0;
      } catch (e) {
        return false;
      }
    },

    sendNewInternalMessage() {
    },

    handleFileUpload() {
      this.newInternalMessage.files = this.$refs.internalMessageFile.files;
    },
  },
};
</script>

<style scoped>
  table tr td:first-child {
    font-weight: 500;
  }
</style>



// WEBPACK FOOTER //
// src/pages/companies/SingleCompany.vue